import { useEffect, useRef } from 'react';
import { getEcho } from './echo';

type ChannelType = 'public' | 'private' | 'presence';

interface UseChannelOptions {
  /** Defaults to 'public'. Use 'private' for private-* channels, 'presence' for presence-* channels. */
  type?: ChannelType;
  /** Additional deps that trigger re-subscription (besides the channel name). */
  deps?: any[];
}

/**
 * Subscribe to a Laravel Echo channel and bind event handlers.
 *
 * Handles cleanup automatically on unmount or when the channel / deps change.
 *
 * @param channel        - The base channel name (without the 'private-' / 'presence-' prefix).
 * @param eventHandlers  - Map of event name → handler function.
 * @param opts           - Optional type + extra dependency array.
 *
 * @example
 * // Private user channel
 * useChannel(`user.${userId}`, { 'notification.sent': (p) => addNotif(p) }, { type: 'private' });
 *
 * // Presence chat channel
 * useChannel(`chat.${roomId}`, { 'chat.message_sent': (m) => appendMsg(m) }, { type: 'presence' });
 */
export function useChannel(
  channel: string,
  eventHandlers: Record<string, (payload: any) => void>,
  opts: UseChannelOptions = {},
): void {
  const { type = 'public', deps = [] } = opts;
  // Keep a stable ref to the handlers to avoid unnecessary re-subscriptions
  const handlersRef = useRef(eventHandlers);
  handlersRef.current = eventHandlers;

  useEffect(() => {
    const echo = getEcho();
    if (!echo || !channel) return;

    // Subscribe to the appropriate channel type
    const sub =
      type === 'private'
        ? echo.private(channel)
        : type === 'presence'
        ? echo.join(channel)
        : echo.channel(channel);

    // Bind each event handler
    for (const [event, _handler] of Object.entries(handlersRef.current)) {
      sub.listen(event, (payload: any) => handlersRef.current[event]?.(payload));
    }

    return () => {
      // Unsubscribe on cleanup
      echo.leave(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, type, ...deps]);
}
